import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Money, TbComparePeriod, TbReportBody } from '@mybooks/ledger';
import { api, unwrap } from '../../api/client';
import { useAccounts, useApiQuery, useContacts, useCurrency } from '../../api/hooks';
import { fmtDate, fmtMoney, fmtReport, money } from '../../lib/format';
import { glLink, type ReportMeta } from './catalog';
import { AsOfFields, CheckPill, RangeFields, ReportFrame, useAsOf, useRange, useReportParams } from './frame';
import { type ReportRow, type ReportTable } from './model';
import { BOOKS_START, rangeLabel, type Range } from './period';
import { AS_OF_COMPARE, asOfColumns, COMPARE, CompactSwitch, CompareSelect, compareColumns, compareDetail, compareParam, compareRow, rangeColumns, stateOf, useApiQueries, varianceNote, type AsOfCompare, type Compare } from './compare';

// ---- API shapes (the generated schema leaves most report rows untyped) ----
interface PnlRow { account_id: string; code: string; name: string; type: 'revenue' | 'expense'; amount: string }
interface Pnl { from: string; to: string; revenue: PnlRow[]; expenses: PnlRow[]; total_revenue: string; total_expenses: string; net_income: string }
interface BsRow { section: string; account_id: string | null; code: string; name: string; amount: string }
interface Bs { as_of: string; assets: BsRow[]; liabilities: BsRow[]; equity: BsRow[]; total_assets: string; total_liabilities: string; total_equity: string; balanced: boolean }
interface AgingRow { contact_id: string; contact_name: string; currency?: string; in_currency?: Record<string, string> | null; current: string; days_1_30: string; days_31_60: string; days_61_90: string; over_90: string; credits: string; total: string }
interface Aging { as_of: string; rows: AgingRow[]; totals: Record<string, string>; ledger_balance: string; revaluation?: string; reconciles: boolean; unapplied_payments?: string }
interface StatementRow { date: string; journal: string; source_type: string; reference: string | null; memo: string | null; debit: string; credit: string; balance: string }
interface Statement { contact: { id: string; name: string }; from: string; to: string; opening_balance: string; closing_balance: string; rows: StatementRow[] }
interface TaxRow { tax_rate_id: string | null; tax_rate_name: string; rate: string; taxable_sales: string; tax: string }
interface SalesTax { from: string; to: string; rows: TaxRow[]; total_taxable_sales: string; total_tax: string }

const data = <T,>(r: { data?: unknown }) => r.data as T;
export const fetchPnl = async (r: Range) => data<Pnl>(await unwrap(api.GET('/v1/reports/profit-and-loss', { params: { query: r } })));
export const fetchBs = async (asOf: string) => data<Bs>(await unwrap(api.GET('/v1/reports/balance-sheet', { params: { query: { as_of: asOf } } })));
/** The trial balance at a date; with compare, also the year-to-date balances at that many earlier periods. */
export const fetchTb = async (asOf: string, compare = 0, period: TbComparePeriod = 'year') =>
  data<TbReportBody>(await unwrap(api.GET('/v1/reports/trial-balance', { params: { query: { as_of: asOf, ...(compare ? { compare, compare_period: period } : {}) } } })));
export const fetchAging = async (side: 'receivable' | 'payable', asOf: string) => data<Aging>(side === 'receivable'
  ? await unwrap(api.GET('/v1/reports/receivables-aging', { params: { query: { as_of: asOf } } }))
  : await unwrap(api.GET('/v1/reports/payables-aging', { params: { query: { as_of: asOf } } })));

export const asOfLabel = (asOf: string) => `As of ${fmtDate(asOf)}`;
const yearStart = (iso: string) => `${iso.slice(0, 4)}-01-01`;

// ---- Profit and loss ----
export function ProfitAndLoss({ meta }: { meta: ReportMeta }) {
  const { range, error, set } = useRange('ytd');
  const { params } = useReportParams();
  const allDates = range.from <= BOOKS_START;
  const compare = (allDates ? 'none' : (params.get('compare') as Compare | null) ?? 'none');
  const [compact, setCompact] = useState(false);
  const accounts = useAccounts();
  const specs = rangeColumns(range, compare);
  const queries = useApiQueries(specs.map((s) => ({ key: ['reports', 'pnl', s.range], fn: () => fetchPnl(s.range), enabled: !error })));
  const cols: { label: string; data: Pnl | undefined; range: Range }[] = specs.map((s, n) => ({ ...s, data: queries[n]?.data }));


  const table = ((): ReportTable | null => {
    if (cols.some((c) => !c.data)) return null;
    const figs = cols.map((c) => c.data as Pnl);
    const byId = new Map((accounts.data ?? []).map((a) => [a.id, a]));
    const all = new Map<string, PnlRow>();
    for (const f of figs) for (const r of [...f.revenue, ...f.expenses]) if (!all.has(r.account_id)) all.set(r.account_id, r);
    const amount = (f: Pnl, id: string) => money([...f.revenue, ...f.expenses].find((r) => r.account_id === id)?.amount);
    const group = (r: PnlRow) => {
      if (r.type === 'revenue') return r.code >= '4900' && r.code < '5000' ? 'other' : 'income';
      return byId.get(r.account_id)?.system_key === 'cost_of_goods_sold' || r.code.startsWith('5') ? 'cos' : 'opex';
    };
    const rowsIn = (g: string) => [...all.values()].filter((r) => group(r) === g).sort((a, b) => a.code.localeCompare(b.code));
    const totalOf = (f: Pnl, g: string) => rowsIn(g).reduce<Money>((s, r) => s + amount(f, r.account_id), 0n);
    const line = (kind: ReportRow['kind'], label: string, get: (f: Pnl) => Money, good: 'up' | 'down' | null, extra: Partial<ReportRow> = {}): ReportRow => {
      const vals = figs.map(get);
      return compareRow(kind, label, vals, vals, good, extra);
    };
    const acctRows = (g: string, good: 'up' | 'down') => rowsIn(g).map((r) => line('ind', r.name, (f) => amount(f, r.account_id), good, { code: r.code, to: glLink(r.account_id, range.from, range.to) }));
    const rows: ReportRow[] = [];
    const gross = (f: Pnl) => totalOf(f, 'income') - totalOf(f, 'cos');
    const operating = (f: Pnl) => gross(f) - totalOf(f, 'opex');
    rows.push({ kind: 'sec', label: 'Income', cells: [] }, ...acctRows('income', 'up'), line('sub', 'Total income', (f) => totalOf(f, 'income'), 'up'));
    if (rowsIn('cos').length) rows.push({ kind: 'sec', label: 'Cost of goods sold', cells: [] }, ...acctRows('cos', 'down'), line('sub', 'Gross profit', gross, 'up'));
    rows.push({ kind: 'sec', label: 'Operating expenses', cells: [] }, ...acctRows('opex', 'down'), line('sub', 'Total operating expenses', (f) => totalOf(f, 'opex'), 'down'), line('sub', 'Operating income', operating, 'up'));
    if (rowsIn('other').length) rows.push({ kind: 'sec', label: 'Other income', cells: [] }, ...acctRows('other', 'up'), line('sub', 'Total other income', (f) => totalOf(f, 'other'), 'up'));
    rows.push(line('grand', 'Net income', (f) => money(f.net_income), 'up', { amountTo: `/reports/balance-sheet?as_of=${range.to}` }));
    return { style: 'ex', columns: compareColumns(cols.map((c) => c.label)), rows };
  })();

  const currency = useCurrency();
  const first = cols[0]?.data;
  const net = first ? money(first.net_income) : null;
  // The rows must add up to the API's own net income; anything else would be a bug worth showing.
  const rowsNet = first ? money(first.total_revenue) - money(first.total_expenses) : null;
  const ended = allDates ? `All dates to ${fmtDate(range.to)}` : `${fmtDate(range.from)} to ${fmtDate(range.to)}`;
  return (
    <ReportFrame pdf={{ key: 'profit-and-loss', query: { from: range.from, to: range.to, compare: compareParam(compare) }, detail: compareDetail(COMPARE, compare) }} meta={meta} period={rangeLabel(range)} state={error ? null : stateOf(...queries)} table={error ? null : table} exTitle={`${ended} · accrual basis`} compact={compact}
      controls={<>
        <RangeFields range={range} error={error} set={set} allowAll />
        <CompareSelect value={compare} options={COMPARE} disabled={allDates} onChange={(v) => set({ compare: v === 'none' ? null : v })} />
        <span className="spacer" />
        <CompactSwitch compact={compact} setCompact={setCompact} />
      </>}
      note={net !== null && rowsNet !== null && <>
        {rowsNet !== net && <CheckPill diff={rowsNet - net} ok="" currency={currency} />}
        <span>{first && first.revenue.length + first.expenses.length === 0 ? 'No income or expenses in this period. ' : ''}Net income for {rangeLabel(range)} is {fmtMoney(net, currency)}. Select an account to see its entries in the general ledger.{cols.length > 1 ? ` ${varianceNote(cols.map((c) => c.label))}` : ''}</span>
      </>} />
  );
}

// ---- Balance sheet ----
const bsKey = (r: BsRow) => r.account_id ?? r.code;
export function BalanceSheet({ meta }: { meta: ReportMeta }) {
  const { asOf, error, set } = useAsOf();
  const { params } = useReportParams();
  const compare = (params.get('compare') as AsOfCompare | null) ?? 'none';
  const [compact, setCompact] = useState(false);
  const currency = useCurrency();
  const specs = asOfColumns(asOf, compare);
  const queries = useApiQueries(specs.map((s) => ({ key: ['reports', 'bs', s.asOf], fn: () => fetchBs(s.asOf), enabled: !error })));
  // Current earnings is every revenue and expense entry to date, so it ties to an all-dates P&L.
  const pnl = useApiQuery(['reports', 'pnl', { from: BOOKS_START, to: asOf }], () => fetchPnl({ from: BOOKS_START, to: asOf }), { enabled: !error });
  const figs = queries.map((q) => q.data);
  const ready = figs.every((f) => f !== undefined);
  const table = ((): ReportTable | null => {
    if (!ready) return null;
    const list = figs as Bs[];
    const ys = yearStart(asOf);
    // Every account that has a balance in any column, in the first column's order, then the rest.
    const section = (key: 'assets' | 'liabilities' | 'equity') => {
      const seen = new Map<string, BsRow>();
      for (const f of list) for (const r of f[key]) if (!seen.has(bsKey(r))) seen.set(bsKey(r), r);
      return [...seen.values()].sort((a, b) => (a.account_id === null ? 1 : b.account_id === null ? -1 : a.code.localeCompare(b.code)));
    };
    const amountIn = (f: Bs, key: 'assets' | 'liabilities' | 'equity', k: string) => money(f[key].find((r) => bsKey(r) === k)?.amount);
    const acctRows = (key: 'assets' | 'liabilities' | 'equity', good: 'up' | 'down' | null) => section(key).map((r): ReportRow => {
      const vals = list.map((f) => amountIn(f, key, bsKey(r)));
      return r.account_id
        ? compareRow('ind', r.name, vals, vals, good, { code: r.code, to: glLink(r.account_id, ys, asOf) })
        : compareRow('ind', r.name, vals, vals, 'up', { amountTo: `/reports/profit-and-loss?from=${BOOKS_START}&to=${asOf}`, secondary: 'All income less expenses to date. Year-end closing is not available yet.' });
    });
    const total = (kind: ReportRow['kind'], label: string, get: (f: Bs) => Money, good: 'up' | 'down' | null) => { const vals = list.map(get); return compareRow(kind, label, vals, vals, good); };
    const rows: ReportRow[] = [
      { kind: 'sec', label: 'Assets', cells: [] }, ...acctRows('assets', null), total('grand', 'Total assets', (f) => money(f.total_assets), null),
      { kind: 'sec', label: 'Liabilities', cells: [] }, ...acctRows('liabilities', 'down'), total('sub', 'Total liabilities', (f) => money(f.total_liabilities), 'down'),
      { kind: 'sec', label: 'Equity', cells: [] }, ...acctRows('equity', 'up'), total('sub', 'Total equity', (f) => money(f.total_equity), 'up'),
      total('grand', 'Total liabilities and equity', (f) => money(f.total_liabilities) + money(f.total_equity), null),
    ];
    return { style: 'ex', columns: compareColumns(specs.map((s) => s.label)), rows };
  })();
  const d = figs[0];
  const earnings = d ? money(d.equity.find((r) => r.account_id === null)?.amount) : null;
  const unbalanced = ready ? (figs as Bs[]).filter((f) => money(f.total_assets) !== money(f.total_liabilities) + money(f.total_equity)) : [];
  return (
    <ReportFrame pdf={{ key: 'balance-sheet', query: { as_of: asOf, compare: compareParam(compare) }, detail: compareDetail(AS_OF_COMPARE, compare) }} meta={meta} period={asOfLabel(asOf)} state={error ? null : stateOf(...queries, pnl)} table={error ? null : table} exTitle={asOfLabel(asOf)} compact={compact}
      controls={<>
        <AsOfFields asOf={asOf} error={error} set={set} />
        <CompareSelect value={compare} options={AS_OF_COMPARE} onChange={(v) => set({ compare: v === 'none' ? null : v })} />
        <span className="spacer" />
        <CompactSwitch compact={compact} setCompact={setCompact} />
      </>}
      note={d && ready && <>
        <CheckPill diff={unbalanced.length ? money(unbalanced[0]!.total_assets) - money(unbalanced[0]!.total_liabilities) - money(unbalanced[0]!.total_equity) : 0n} ok={specs.length > 1 ? 'Balanced in every column' : 'Balanced'} currency={currency} />
        <span>Assets equal liabilities plus equity.</span>
        {pnl.data && earnings !== null && <><CheckPill diff={earnings - money(pnl.data.net_income)} ok="Ties to profit and loss" currency={currency} /><span>Current earnings equal net income for all dates to {fmtDate(asOf)}.</span></>}
        {specs.length > 1 && <span>{varianceNote(specs.map((s) => s.label))}</span>}
      </>} />
  );
}

// ---- Aging ----
type AgingKey = 'current' | 'days_1_30' | 'days_31_60' | 'days_61_90' | 'over_90' | 'credits' | 'total';
/**
 * One aging row per contact and currency (ADR 0029). The cells are base, so rows add up to the
 * totals; a foreign row also names its currency and shows its own total under the base one.
 */
export function agingRow(r: AgingRow, keys: readonly AgingKey[], base: string): ReportRow {
  const foreign = r.in_currency && r.currency && r.currency !== base ? r.currency : null;
  const row: ReportRow = { kind: 'row', label: foreign ? `${r.contact_name} (${foreign})` : r.contact_name, to: `/contacts/${r.contact_id}`, cells: keys.map((k) => money(r[k])) };
  if (foreign && r.in_currency) row.cellNotes = { [keys.length - 1]: fmtReport(r.in_currency.total, foreign) };
  return row;
}

export function AgingReport({ meta, side }: { meta: ReportMeta; side: 'receivable' | 'payable' }) {
  const { asOf, error, set } = useAsOf();
  const currency = useCurrency();
  const q = useApiQuery(['reports', 'aging', side, asOf], () => fetchAging(side, asOf), { enabled: !error });
  const table = useMemo<ReportTable | null>(() => {
    const d = q.data;
    if (!d) return null;
    const hasCredits = money(d.totals.credits) !== 0n;
    const keys: AgingKey[] = ['current', 'days_1_30', 'days_31_60', 'days_61_90', 'over_90', ...(hasCredits ? ['credits' as const] : []), 'total'];
    const heads = ['Current', '1–30 days', '31–60 days', '61–90 days', 'Over 90 days', ...(hasCredits ? ['Credits'] : []), 'Total'];
    return {
      style: 'fin', dashZero: true,
      columns: [{ label: side === 'receivable' ? 'Customer' : 'Supplier' }, ...heads.map((label) => ({ label, align: 'r' as const }))],
      rows: [
        ...d.rows.map((r): ReportRow => agingRow(r, keys, currency)),
        { kind: 'grand', label: 'Total', cells: keys.map((k) => money(d.totals[k])) },
      ],
    };
  }, [q.data, side, currency]);
  const d = q.data;
  const control = side === 'receivable' ? 'Accounts Receivable' : 'Accounts Payable';
  return (
    <ReportFrame pdf={{ key: side === 'receivable' ? 'receivables-aging' : 'payables-aging', query: { as_of: asOf } }} meta={meta} period={asOfLabel(asOf)} state={error ? null : stateOf(q)} table={error ? null : table}
      controls={<AsOfFields asOf={asOf} error={error} set={set} />}
      empty={<div className="empty">{side === 'receivable' ? 'Nobody owes you anything' : 'You owe nothing'} as of {fmtDate(asOf)}.</div>}
      note={d && <>
        <CheckPill diff={money(d.totals.total) + money(d.revaluation) - money(d.ledger_balance)} ok="Matches the ledger" currency={currency} />
        <span>{money(d.revaluation) !== 0n
          ? <>The total plus this day's currency revaluation ({fmtMoney(d.revaluation, currency)}) equals {control} on the balance sheet: {fmtMoney(d.ledger_balance, currency)}.</>
          : <>The total equals {control} on the balance sheet: {fmtMoney(d.ledger_balance, currency)}.</>} Select a {side === 'receivable' ? 'customer' : 'supplier'} to see their documents.</span>
        {d.unapplied_payments && money(d.unapplied_payments) !== 0n && <span>{fmtMoney(d.unapplied_payments, currency)} in payments received is not applied to an invoice yet and sits in Customer Deposits.</span>}
        <Link className="link-btn" to={side === 'receivable' ? '/invoices?filter=open' : '/bills?filter=open'}>{side === 'receivable' ? 'Open invoices' : 'Unpaid bills'}</Link>
      </>} />
  );
}

// ---- Customer statement ----
const SOURCE_NAMES: Record<string, string> = { invoice: 'Invoice', payment: 'Payment', credit_note: 'Credit note', refund: 'Refund', customer_refund: 'Refund', credit_allocation: 'Credit applied' };
export function CustomerStatement({ meta }: { meta: ReportMeta }) {
  const { range, error, set } = useRange('ytd');
  const { params } = useReportParams();
  const contacts = useContacts();
  const customers = (contacts.data ?? []).filter((c) => c.type !== 'vendor').sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
  const contactId = params.get('contact') ?? customers.find((c) => c.is_active)?.id ?? '';
  const contact = customers.find((c) => c.id === contactId);
  const ok = !error && !!contactId;
  const q = useApiQuery(['reports', 'statement', contactId, range], async () => data<Statement>(await unwrap(api.GET('/v1/reports/statement', { params: { query: { contact_id: contactId, side: 'receivable', from: range.from, to: range.to } } }))), { enabled: ok });
  const table = useMemo<ReportTable | null>(() => {
    const d = q.data;
    if (!d) return null;
    const rows: ReportRow[] = [{ kind: 'sub', label: `Opening balance, ${fmtDate(d.from)}`, cells: [null, null, null, money(d.opening_balance)] }];
    for (const r of d.rows) {
      const what = `${SOURCE_NAMES[r.source_type] ?? 'Journal entry'}${r.reference ? ` ${r.reference}` : ''}`;
      rows.push({ kind: 'row', label: fmtDate(r.date), cells: [what, money(r.debit) === 0n ? null : money(r.debit), money(r.credit) === 0n ? null : money(r.credit), money(r.balance)] });
    }
    rows.push({ kind: 'grand', label: 'Amount due', cells: [null, null, null, money(d.closing_balance)] });
    return { style: 'fin', columns: [{ label: 'Date' }, { label: 'Activity' }, { label: 'Charges', align: 'r' }, { label: 'Payments', align: 'r' }, { label: 'Balance', align: 'r' }], rows };
  }, [q.data]);
  const address = contact ? [contact.address_line1, contact.city, contact.region, contact.postal_code].filter(Boolean).join(', ') : '';
  return (
    <ReportFrame pdf={{ key: 'statement', query: { contact_id: contactId, side: 'receivable', from: range.from, to: range.to }, detail: contact?.name }} meta={meta} period={rangeLabel(range)} state={ok ? stateOf(q) : contacts.isLoading ? stateOf(contacts) : null} table={ok ? table : null}
      fileTag={`${contact?.name ?? ''} ${rangeLabel(range)}`}
      empty={<div className="empty">{customers.length ? 'Choose a customer.' : <>No customers yet. <Link className="link-btn" to="/customers?new=1">Add a customer</Link></>}</div>}
      controls={<>
        <div className="field" style={{ minWidth: 240 }}>
          <label htmlFor="stmt-customer">Customer</label>
          <select className="input" id="stmt-customer" value={contactId} onChange={(e) => set({ contact: e.target.value })}>
            {!contactId && <option value="">Choose a customer</option>}
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.is_active ? '' : ' (inactive)'}</option>)}
          </select>
        </div>
        <RangeFields range={range} error={error} set={set} />
      </>}
      head={contact && (address || contact.email) ? <div className="muted ledger-hint">{contact.name}{address ? ` · ${address}` : ''}{contact.email ? ` · ${contact.email}` : ''}</div> : null}
      note={q.data && <span>Charges are invoices; payments and credits reduce the balance. <Link className="link-btn" to={`/contacts/${contactId}`}>Open {contact?.name ?? 'customer'}</Link></span>} />
  );
}

// ---- Sales tax ----
export function SalesTaxSummary({ meta }: { meta: ReportMeta }) {
  const { range, error, set } = useRange('ytd');
  const accounts = useAccounts();
  const q = useApiQuery(['reports', 'sales-tax', range], async () => data<SalesTax>(await unwrap(api.GET('/v1/reports/sales-tax', { params: { query: range } }))), { enabled: !error });
  const table = useMemo<ReportTable | null>(() => {
    const d = q.data;
    if (!d) return null;
    const pct = (rate: string) => `${rate.replace(/\.?0+$/, '')}%`;
    return {
      style: 'fin', columns: [{ label: 'Tax rate' }, { label: 'Rate', align: 'r' }, { label: 'Taxable sales', align: 'r' }, { label: 'Tax collected', align: 'r' }],
      rows: [
        ...d.rows.map((r): ReportRow => ({ kind: 'row', label: r.tax_rate_name, cells: [r.tax_rate_id ? pct(r.rate) : '—', money(r.taxable_sales), money(r.tax)] })),
        { kind: 'grand', label: 'Total', cells: [null, money(d.total_taxable_sales), money(d.total_tax)] },
      ],
    };
  }, [q.data]);
  const taxAccount = accounts.data?.find((a) => a.system_key === 'sales_tax_payable');
  return (
    <ReportFrame pdf={{ key: 'sales-tax', query: { from: range.from, to: range.to } }} meta={meta} period={rangeLabel(range)} state={error ? null : stateOf(q)} table={error ? null : table}
      controls={<RangeFields range={range} error={error} set={set} />}
      empty={<div className="empty">No sales in this period.</div>}
      note={q.data && <span>Tax collected is what sent invoices charged in the period, less credit notes. {taxAccount && <>Payments to the tax authority are in the <Link className="link-btn" to={glLink(taxAccount.id, range.from, range.to)}>Sales Tax Payable ledger</Link>.</>}</span>} />
  );
}

